/**
 * Creates an organic, splatter-like distortion with donut shape
 * @param {number} x - X coordinate
 * @param {number} y - Y coordinate
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {number} time - Animation time
 * @param {number} scroll - Scroll position
 * @returns {Object} Distorted point {x, y}
 */
export const distortAOrganic = (x, y, width, height, time, scroll) => {
  const refSize = Math.max(width, height);
  const offsetX = (width - refSize) / 2;
  const offsetY = (height - refSize) / 2;
  const normalx = (x - offsetX) / refSize;
  const normaly = (y - offsetY) / refSize;

  const dx = normalx - 0.5;
  const dy = normaly - 0.5;
  const dd = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx);

  // Position-seeded pseudo-random values for organic variation
  const seed1 = Math.sin(x * 0.0234 + y * 0.0456) * 1000;
  const seed2 = Math.cos(x * 0.0567 - y * 0.0345) * 1000;
  const seed3 = Math.sin(x * 0.0123 + y * 0.0789) * 1000;

  // Random angular offsets per particle (seeded on position)
  const randomAngleOffset = (seed1 + seed2) * 0.1;
  const randomMagnitude = Math.abs(Math.sin(seed1) * Math.cos(seed2)) * 0.15;
  const randomPulse = Math.sin(seed3 + time * 0.0005) * 0.08;

  // Layered angular chaos for irregular edges
  const chaos1 = Math.sin(angle * 8 + time * 0.001 + randomAngleOffset) * (0.12 + randomMagnitude);
  const chaos2 = Math.sin(angle * 17 + scroll / 40 + seed1 * 0.01) * (0.08 + randomPulse);
  const chaos3 = Math.sin(angle * 31 - time * 0.0008 + scroll / 60 + seed2 * 0.01) * (0.15 + randomMagnitude);
  const angularChaos = chaos1 + chaos2 + chaos3;

  // Clear donut: push inward if too far out, push outward if too close in
  const innerRadius = 0.25;
  const outerRadius = 0.55;
  const midRadius = (innerRadius + outerRadius) / 2;

  let donutPush = 0;
  if (dd < innerRadius) {
    // Inside hole - push outward
    donutPush = (innerRadius - dd) * 0.8;
  } else if (dd > outerRadius) {
    // Outside ring - push inward
    donutPush = -(dd - outerRadius) * 0.8;
  }

  const totalOffset = (donutPush + angularChaos) * refSize;

  return {
    x: x + (dx / dd) * totalOffset,
    y: y + (dy / dd) * totalOffset
  };
};
